import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadAircraftWiseStockReportHistoryCsv,
  downloadAircraftWiseStockReportHistoryPdf,
  downloadAircraftWiseStockReportCsv,
  downloadAircraftWiseStockReportPdf,
  fetchAircraftWiseStockReportPreviewData,
  fetchAircraftWiseStockReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'IN_INVENTORY', label: 'In Inventory' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'QUARANTINED', label: 'Quarantined' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'UNAVAILABLE', label: 'Unavailable' }
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'PART', label: 'Part' },
  { value: 'TOOL', label: 'Tool' },
  { value: 'MATERIAL', label: 'Material' },
  { value: 'CONSUMABLE', label: 'Consumable' },
  { value: 'GSE', label: 'GSE' }
];

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Dedicated Aircraft-wise Stock Report — summary by aircraft plus line-level detail.
 */
const AircraftWiseStockReportPanel = ({ aircraftOptions = [], aircraftLabelById = {} }) => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [stockAircraftOptions, setStockAircraftOptions] = useState(aircraftOptions);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/report-center/filter-options/aircraft-with-gen', {
          skipGlobalErrorToast: true
        });
        if (!cancelled && Array.isArray(res.data?.options)) {
          setStockAircraftOptions(res.data.options);
        }
      } catch {
        if (!cancelled) setStockAircraftOptions(aircraftOptions);
      }
    })();
    return () => { cancelled = true; };
  }, [aircraftOptions]);

  const stockAircraftLabelById = useMemo(
    () => ({
      ...aircraftLabelById,
      ...Object.fromEntries(stockAircraftOptions.map((a) => [String(a.value), a.label]))
    }),
    [aircraftLabelById, stockAircraftOptions]
  );

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchAircraftWiseStockReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const fileStamp = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : reportDate;

  const reportPayload = useMemo(() => ({
    aircraftIds: selectedAircraftIds,
    includeArchived,
    status,
    category,
    search,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate
  }), [selectedAircraftIds, includeArchived, status, category, search, dateFrom, dateTo, reportDate]);

  const handlePreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchAircraftWiseStockReportPreviewData(reportPayload);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchAircraftWiseStockReportPreviewData(reportPayload);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadAircraftWiseStockReportPdf(
        { ...reportPayload, visualizations },
        `Aircraft_Wise_Stock_Report_${fileStamp}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'Aircraft-wise Stock Report with visualizations downloaded and saved to history'
          : 'Aircraft-wise Stock Report downloaded and saved to history',
        'success'
      );
      if (subTab === 1) {
        loadHistory();
      }
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadAircraftWiseStockReportCsv(
        reportPayload,
        `Aircraft_Wise_Stock_Report_${fileStamp}.pdf`
      );
      showToast('Aircraft-wise Stock Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryDownload = (row) => {
    try {
      downloadAircraftWiseStockReportHistoryPdf(row._id, row.filename || 'Aircraft_Wise_Stock_Report.pdf');
      showToast('Re-downloading stored PDF', 'success');
    } catch (err) {
      showToast(err.message || 'Re-download failed', 'error');
    }
  };

  const handleHistoryDownloadCsv = (row) => {
    try {
      downloadAircraftWiseStockReportHistoryCsv(row._id, row.filename || 'Aircraft_Wise_Stock_Report.pdf');
      showToast('Downloading CSV', 'success');
    } catch (err) {
      showToast(err.message || 'CSV download failed', 'error');
    }
  };

  const busy = previewing || downloading || downloadingCsv || visualizing;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Aircraft-wise Stock Report
      </Typography>
      <Typography color="text.secondary" paragraph>
        Store stock summarised by aircraft (including GEN general stock), plus line-level detail.
        Leave aircraft blank for the full fleet. Each PDF download is stored for later re-download.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5} maxWidth={640}>
            <FormControl fullWidth>
              <InputLabel id="aircraft-wise-stock-aircrafts-label">Aircrafts</InputLabel>
              <Select
                labelId="aircraft-wise-stock-aircrafts-label"
                multiple
                value={selectedAircraftIds}
                label="Aircrafts"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedAircraftIds(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All aircraft + GEN';
                  return selected.map((id) => stockAircraftLabelById[id] || id).join(', ');
                }}
              >
                {stockAircraftOptions.map((ac) => (
                  <MenuItem key={ac.value} value={ac.value}>
                    {ac.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Optional — includes GEN (general stock) when available in the list
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="aircraft-wise-stock-status-label">Status</InputLabel>
              <Select
                labelId="aircraft-wise-stock-status-label"
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="aircraft-wise-stock-category-label">Category</InputLabel>
              <Select
                labelId="aircraft-wise-stock-category-label"
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="Part number, store code, description…"
              helperText="Filters summary and detail tables"
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                />
              )}
              label="Include archived items"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
              <TextField
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
            </Stack>

            {dateRangeError && (
              <Alert severity="warning">{dateRangeError}</Alert>
            )}

            <TextField
              label="Report Date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />}
                onClick={handleVisualize}
                disabled={busy || Boolean(dateRangeError)}
              >
                Visualize
              </Button>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <Preview />}
                onClick={handlePreview}
                disabled={busy || Boolean(dateRangeError)}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                onClick={() => handleDownload()}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />}
                onClick={handleDownloadCsv}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded Aircraft-wise Stock Reports</Typography>
            <Button
              size="small"
              startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={loadHistory}
              disabled={historyLoading}
            >
              Refresh
            </Button>
          </Box>

          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">
              No Aircraft-wise Stock Report downloads yet. Generate one from the Generate tab.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Aircrafts</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {(row.aircraftLabels || []).length
                        ? row.aircraftLabels.join(', ')
                        : 'All'}
                    </TableCell>
                    <TableCell>{row.status || 'All'}</TableCell>
                    <TableCell>{row.category || 'All'}</TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownload(row)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownloadCsv(row)}
                        >
                          CSV
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="Aircraft-wise Stock Report preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
        onDownload={() => handleDownload()}
        downloading={downloading}
        onDownloadCsv={handleDownloadCsv}
        downloadingCsv={downloadingCsv}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="Aircraft-wise Stock Report"
        data={visualizeData}
        downloading={downloading}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdfWithVisualization={(visualizations) => handleDownload(visualizations)}
      />
    </Box>
  );
};

export default AircraftWiseStockReportPanel;
